import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  CopyIcon,
  CheckIcon,
  XIcon,
  ShareNetworkIcon,
  CircleNotchIcon,
} from '@phosphor-icons/react';
import {
  useCreateShareableLink,
  getShareableLinkUrl,
} from 'src/api/core/useShareableLinks';
import {
  MultiSelect,
  MultiSelectOption,
} from '@/components/common/MultiSelect';
import { SingleSelect } from '@/components/common/SingleSelect';
import useDivisions from '@/api/organization/useDivisions';
import useRebateAgreementYears from '@/api/rebates/useRebateAgreementYears';

type ReportLineType = 'estimates' | 'predictions' | 'reported';

const LINE_TYPE_OPTIONS: {
  id: ReportLineType;
  label: string;
  value: ReportLineType;
}[] = [
  { id: 'reported', label: 'Reported', value: 'reported' },
  { id: 'estimates', label: 'Estimates', value: 'estimates' },
  { id: 'predictions', label: 'Predictions', value: 'predictions' },
];

interface ShareReportModalProps {
  open: boolean;
  onClose: () => void;
  reportId: string;
  reportTitle: string;
}

export function ShareReportModal({
  open,
  onClose,
  reportId,
  reportTitle,
}: ShareReportModalProps) {
  const currentYear = new Date().getFullYear();

  // Link name state
  const [linkName, setLinkName] = useState('');

  // Email state
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Filter state
  const [selectedYear, setSelectedYear] = useState<number>(currentYear);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedLineType, setSelectedLineType] =
    useState<ReportLineType>('reported');

  const createShareableLink = useCreateShareableLink();

  // Fetch divisions for filter
  const { data: divisionsData, isLoading: divisionsLoading } =
    useDivisions(true);

  // Fetch available years
  const { data: availableYearsData } = useRebateAgreementYears();

  // Prepare division options
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((division) => ({
      id: division.id,
      label: division.name,
      value: division,
      group: division.tags?.[0]?.name,
    }));
  }, [divisionsData?.divisions]);

  // Prepare year options
  const yearOptions = useMemo(() => {
    const years =
      availableYearsData && 'years' in availableYearsData
        ? availableYearsData.years
        : [];
    // Ensure current year is always available
    const allYears = [...new Set([currentYear, ...years])].sort(
      (a, b) => b - a,
    );
    return allYears.map((year) => ({
      id: year.toString(),
      label: year.toString(),
      value: year,
    }));
  }, [availableYearsData, currentYear]);

  // Determine which filters to show based on report type
  const showYearFilter = true; // All reports use year
  const showDivisionFilter = true; // All reports support division filtering
  const showLineTypeFilter =
    reportId === 'division-rebate-summary' ||
    reportId === 'supplier-tag-summary';

  function handleAddEmail() {
    const trimmedEmail = emailInput.trim().toLowerCase();
    if (
      trimmedEmail &&
      isValidEmail(trimmedEmail) &&
      !emails.includes(trimmedEmail)
    ) {
      setEmails([...emails, trimmedEmail]);
      setEmailInput('');
    }
  }

  function handleRemoveEmail(emailToRemove: string) {
    setEmails(emails.filter((email) => email !== emailToRemove));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  }

  function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async function handleCreateLink() {
    // Build metadata based on selected filters
    const metadata: Record<string, unknown> = {
      year: selectedYear,
    };

    if (selectedDivisions.length > 0) {
      metadata.divisionIds = selectedDivisions.map((d) => d.id);
    }

    if (showLineTypeFilter) {
      metadata.lineType = selectedLineType;
    }

    const result = await createShareableLink.mutateAsync({
      name: linkName.trim() || null,
      resourceType: `report:${reportId}`,
      metadata,
      allowedEmails: emails,
    });

    if (result.data?.shareableLink) {
      const url = getShareableLinkUrl(result.data.shareableLink.token);
      setGeneratedLink(url);
    }
  }

  function handleCopyLink() {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }

  function handleClose() {
    setLinkName('');
    setEmails([]);
    setEmailInput('');
    setGeneratedLink(null);
    setCopied(false);
    setSelectedYear(currentYear);
    setSelectedDivisions([]);
    setSelectedLineType('reported');
    onClose();
  }

  const canCreateLink = emails.length > 0;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShareNetworkIcon className="h-5 w-5" />
            Share Report
          </DialogTitle>
          <DialogDescription>
            Share &quot;{reportTitle}&quot; with specific people. They will need
            to log in to view this report.
          </DialogDescription>
        </DialogHeader>

        {!generatedLink ? (
          <div className="space-y-4">
            {/* Link Name */}
            <div className="space-y-2">
              <Label htmlFor="linkName">Link Name (optional)</Label>
              <Input
                id="linkName"
                placeholder="e.g., Q4 2024 Rebate Summary for Acme Corp"
                value={linkName}
                onChange={(e) => setLinkName(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Give this link a descriptive name. Recipients will see this as
                the report title.
              </p>
            </div>

            {/* Email Section */}
            <div className="space-y-2">
              <Label htmlFor="email">Share with (email addresses)</Label>
              <div className="flex gap-2">
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter email address"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddEmail}
                  disabled={
                    !emailInput.trim() || !isValidEmail(emailInput.trim())
                  }
                >
                  Add
                </Button>
              </div>
              {emails.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {emails.map((email) => (
                    <Badge
                      key={email}
                      variant="secondary"
                      className="flex items-center gap-1 pr-1"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        className="ml-1 rounded-full hover:bg-muted p-0.5"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              {emails.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Add at least one email address to share this report.
                </p>
              )}
            </div>

            {/* Filters Section */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-3 block">
                Report Filters
              </Label>
              <p className="text-sm text-muted-foreground mb-3">
                Set the default filters for this shared report. Recipients will
                see the report with these filters applied.
              </p>
              <div className="space-y-3">
                {showYearFilter && (
                  <SingleSelect
                    label="Year"
                    options={yearOptions}
                    value={
                      yearOptions.find((opt) => opt.value === selectedYear) ||
                      null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedYear(option.value);
                      }
                    }}
                    placeholder="Select year..."
                    searchPlaceholder="Search years..."
                  />
                )}

                {showDivisionFilter && (
                  <MultiSelect
                    label="Divisions"
                    options={divisionOptions}
                    value={selectedDivisions}
                    onChange={setSelectedDivisions}
                    placeholder="All divisions (no filter)"
                    searchPlaceholder="Search divisions..."
                    disabled={divisionsLoading}
                  />
                )}

                {showLineTypeFilter && (
                  <SingleSelect
                    label="Line Type"
                    options={LINE_TYPE_OPTIONS}
                    value={
                      LINE_TYPE_OPTIONS.find(
                        (opt) => opt.value === selectedLineType,
                      ) || null
                    }
                    onChange={(option) => {
                      if (option) {
                        setSelectedLineType(option.value);
                      }
                    }}
                    placeholder="Select line type..."
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Shareable link</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={generatedLink}
                  className="font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                >
                  {copied ? (
                    <CheckIcon className="h-4 w-4 text-green-600" />
                  ) : (
                    <CopyIcon className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="text-sm text-muted-foreground space-y-1">
              {linkName.trim() && (
                <p>
                  <strong>Name:</strong> {linkName.trim()}
                </p>
              )}
              <p>
                <strong>Shared with:</strong> {emails.join(', ')}
              </p>
              <p>
                <strong>Year:</strong> {selectedYear}
              </p>
              {selectedDivisions.length > 0 && (
                <p>
                  <strong>Divisions:</strong>{' '}
                  {selectedDivisions.map((d) => d.label).join(', ')}
                </p>
              )}
              {showLineTypeFilter && (
                <p>
                  <strong>Line Type:</strong>{' '}
                  {
                    LINE_TYPE_OPTIONS.find((o) => o.value === selectedLineType)
                      ?.label
                  }
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {generatedLink ? 'Done' : 'Cancel'}
          </Button>
          {!generatedLink && (
            <Button
              onClick={handleCreateLink}
              disabled={createShareableLink.isPending || !canCreateLink}
            >
              {createShareableLink.isPending && (
                <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
